import React = require('react');
import { useEffect, useState } from 'react';
import { ChecklistItem } from './components/ChecklistItem';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import axios from 'axios';
import { url } from '../../../../data/api/util';
import { Endpoints } from '../../../../data/api/endpoints';
import { toastr } from 'react-redux-toastr';
import { FaSpinner } from 'react-icons/fa';
import { MTAChangeDetails } from '../MakeAChange/components/MakeAChangeSelection/Components/MTAChangeDetails';
import { get } from 'lodash';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { WhatsCovered } from '../Components/WhatsCovered';
import { GoBack } from '../Components/GoBack';
import { mtaActions, setSelectedPolicy } from '../../../../services/mta/actions';
import { canUpsellPolicy, handleClick } from '../MakeAChange/components/MakeAChangeSelection/MTAHelpers';
import Heap from '../../../../helpers/Heap';
import { MtaSelection } from '../MakeAChange/components/MakeAChangeSelection';
import { destroy, reset } from 'redux-form';

const HolidayChecklist = ({ auth, branding, actions, history, mta }) => {
  const [policy, setPolicy] = useState(null);
  const [checklistState, setChecklistState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState([]);

  const checklistItems = [
    {
      name: 'Travel Insurance',
      key: 'travel_insurance',
      icon: '',
    },
    {
      name: 'Passports in date?',
      key: 'passports_in_date',
      icon: 'passport.svg',
    },
    {
      name: 'GHIC Card?',
      key: 'ghic_card',
      icon: 'medical-card.svg',
    },
    {
      name: 'Vaccinated? (If Applicable)',
      key: 'vaccinated',
      icon: 'vaccinated.svg',
      url: 'https://www.nhs.uk/vaccinations/travel-vaccinations/travel-vaccination-advice/',
      link: 'Travel vaccination advice - NHS',
    },
    {
      name: 'Visas?',
      key: 'visas',
      icon: 'visa.svg',
    },
    {
      name: 'Currency?',
      key: 'currency',
      icon: 'currency.svg',
    },
    {
      name: 'IDP (International Driving Permit)',
      key: 'idp',
      icon: 'license.svg',
    },
    {
      name: 'Taking medication?',
      key: 'medication',
      icon: 'medicine.svg',
    },
    {
      name: 'Airport assistance?',
      key: 'airport_assistance',
      icon: 'help-desk.svg',
    },
    {
      name: 'FCDO travel advice link',
      key: 'fcdo',
      icon: 'guides.svg',
      url: 'https://www.gov.uk/foreign-travel-advice',
      link: 'Foreign travel advice – GOV UK',
      skipCheckbox: true,
    },
  ];

  // Track page load
  useEffect(() => {
    Heap.trackEvent('ChecklistView', policy, 'policy')
  }, [])

  // Load Policy data
  useEffect(() => {
    const matchedPolicy = auth.customer.policies.find(p => location.pathname.includes(p.id));
    if (matchedPolicy && matchedPolicy.id !== policy?.id) {
      setPolicy(matchedPolicy);
      actions.dispatch(setSelectedPolicy(matchedPolicy))
    }
  }, [location.pathname, auth.customer.policies]);

  // Fetch Checklist data
  useEffect(() => {
    const fetchChecklist = async () => {
      setIsLoading(true)

      try {
        const response = await axios.get(
          url(Endpoints.HOLIDAY_CHECKLIST, { params: { id: policy.id } }),
          { headers: { 'Content-Type': 'application/vnd.api+json' } }
        );
        setChecklistState(response.data.data)
        setIsLoading(false)

        return response;
      } catch (err) {
        toastr.error('Failed to load checklist');
        setIsLoading(false)
      }
    };

    if (!policy) {
      return;
    }

    fetchChecklist();
  }, [policy]);


  const addItemToLoadingList = (key: string) => {
    setLoadingItems(prevState => {
      return [
        ...prevState,
        key,
      ];
    })
  }

  const removeItemFromLoadingList = (key: string) => {
    setLoadingItems(prevState => {
      return prevState.filter(item => item !== key)
    })
  }

  const isItemChecked = (key: string) => {
    return checklistState.some(item => item.attributes.key === key)
  }

  const isItemLoading = (key: string) => {
    return loadingItems.some(item => item === key)
  }

  const getItemId = (key: string) => {
    return checklistState.find(item => item.attributes.key === key)?.id
  }

  const storeItem = (key: string) => {
    addItemToLoadingList(key);

    Heap.trackEvent('ChecklistItemChecked', policy, 'policy', {key})

    axios.post(
      url(Endpoints.HOLIDAY_CHECKLIST, { params: { id: policy.id } }),
      { key },
    ).then((response) => {
      setChecklistState(prevState => {
        return [
          ...prevState,
          response.data.data
        ]
      })
    }).catch((err) => {
      toastr.error('Error updating checklist')
    }).finally(() => {
      removeItemFromLoadingList(key);
    })
  }

  const deleteItem = (key, id) => {
    addItemToLoadingList(key);

    Heap.trackEvent('ChecklistItemUnchecked', policy, 'policy', {key})

    axios.delete(
      url(Endpoints.DELETE_HOLIDAY_CHECKLIST_ITEM, { params: { id } }),
    ).then((response) => {
      setChecklistState(prevState => {
        return prevState.filter((item) => item.id !== id)
      })
    }).catch((err) => {
      toastr.error('Error updating checklist')
    }).finally(() => {
      removeItemFromLoadingList(key);
    })
  }

  const handleToggle = (key, id) => {
    if (isItemLoading(key)) {
      return;
    }

    if (isItemChecked(key)) {
      deleteItem(key, id)
      return;
    }
    storeItem(key);
  }

  const handleUpsell = () => {
    Heap.trackEvent('PolicyUpsellFromChecklist', policy, 'policy')
    actions.dispatch(push(`/account/redirect-to-mta-upsell/${policy.id}`))
  }

  const handleOptionsLink = () => {
    Heap.trackEvent('PolicyOptionsFromChecklist', policy, 'policy')
    actions.dispatch(handleClick(policy.id, actions, MtaSelection.ADDITIONAL_OPTION, null, null))
  }

  const logItemLinkClick = (key) => {
    Heap.trackEvent('ChecklistItemLinkClick', policy, 'policy', {key})
  }

  if (!policy && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (<FaSpinner className="fa-spin" style={{ padding: '20px', margin: 'auto' }}/>)
  }

  const customerMTAOptions = get(branding.channel.products[0], 'metadata.direct_customer_mta_options', null);
  const canUpsell = canUpsellPolicy(policy, mta, customerMTAOptions);

  return (
    <div className={'holiday-checklist-page'}>
      <div className={'section-group'}>
        {checklistItems.map((item, i) => {
          return (<>
              <ChecklistItem
                id={getItemId(item.key)}
                name={item.name}
                itemKey={item.key}
                key={item.key}
                icon={item.icon}
                link={item.link}
                url={item.url}
                isChecked={isItemChecked(item.key)}
                toggleCheckbox={handleToggle}
                logLinkClick={logItemLinkClick}
                hasCheckbox={!item.skipCheckbox}
              />
              {i < checklistItems.length - 1 && <hr/>}
          </>
            )
        })}
      </div>
      <div className={'section-group upsell-buttons-section'}>
        <h2>Do you need additional cover?</h2>

        <div className={'upsell-buttons'}>
          {canUpsell && (
            <div className={'upsell-button'}>
              <MTAChangeDetails
                text={'Upgrade to Annual Multi Trip Cover'}
                icon={'world.svg'}
                handleClick={handleUpsell}
                enabled={customerMTAOptions?.change_trip_type?.enabled}
              />
            </div>
          )}

          <div className={'upsell-button'}>
            <MTAChangeDetails
              text={'Add Optional Extras'}
              icon={'shield.svg'}
              handleClick={handleOptionsLink}
              enabled={customerMTAOptions?.change_trip_type?.enabled}
            />
          </div>
        </div>

      </div>

      <div style={{marginTop: '15px'}}/>
      <WhatsCovered/>

      <div style={{marginTop: '15px'}}/>
      <GoBack history={history}/>
    </div>
  )
}

export default connect(
  (state: IStore) => {
    return {
      auth: state.auth,
      branding: state.branding,
      mta: state.mta
    };
  },
  (dispatch) => ({
    actions: {
      dispatch,
      push: bindActionCreators(push, dispatch),
      reset: bindActionCreators(reset, dispatch),
      destroy: bindActionCreators(destroy, dispatch),
      mta: bindActionCreators({ ...mtaActions }, dispatch),
    }
  }))(HolidayChecklist);
